import {isString} from "lodash"
import env from "../../../env"
import getUrlForEnv, {services} from "../../../endpoints/getUrlForEnv"
import EnvVariables from "../../../env/interface/env"
import {PRODUCTION, STAGING, DEVELOPMENT} from "../../../constants/environment"

/**
 * Gets the GraphQL URL based on environment configuration and graph cookie (if present).
 *
 * The function returns an appropriate GraphQL URL by checking the GRAPH_QL value (typically from a cookie).
 * Special values are handled:
 * - "local": Returns localhost URL for local development
 * - "v2": Uses the v2 API path instead of the default v1
 *
 * In production, all overrides are ignored except "local" and "v2". In non-production environments,
 * the function supports branch name overrides (converted to service URLs) and full HTTP URL overrides.
 */
export default function getUrl({
  env: providedEnv
}: {
  env?: EnvVariables
} = {}) {
  const envToUse = providedEnv || env
  const graphQLTrimmed = isString(envToUse.GRAPH_QL) ? envToUse.GRAPH_QL.trim() : ""
  const isProduction = envToUse.ENVIRONMENT_NAME === PRODUCTION
  const path = getApiPath(envToUse.GRAPH_QL)

  // Handle "local" override - always returns early (allowed in production)
  if (graphQLTrimmed === "local") {
    return `http://${envToUse.GET_LOCAL}:3001${path}`
  }

  // Production environment: ignore all overrides except "local" and "v2" (handled above)
  if (isProduction) {
    return appendPath(getUrlForEnv(services.GRAPH_QL_SERVICE, ""), path)
  }

  // Handle branch name override
  if (isBranchNameOverride(graphQLTrimmed)) {
    return buildBranchUrl(graphQLTrimmed, envToUse.ENVIRONMENT_NAME, path)
  }

  // Handle HTTP URL override
  if (isString(envToUse.GRAPH_QL) && graphQLTrimmed !== "" && graphQLTrimmed.startsWith("http")) {
    return handleHttpUrlOverride(graphQLTrimmed, path)
  }

  // Handle local development
  if (envToUse.IS_LOCAL) {
    return `http://${envToUse.GET_LOCAL}:3001${path}`
  }

  return appendPath(getUrlForEnv(services.GRAPH_QL_SERVICE, ""), path)
}

/**
 * Converts a branch name to a URL-safe slug
 * Replaces special characters (except underscores) with hyphens
 */
function branchToSlug(branch: string): string {
  return branch.replace(/[^a-zA-Z0-9_-]/g, "-")
}

/**
 * Determines the API version path (v1 or v2) based on GRAPH_QL configuration
 */
function getApiPath(graphQL: string | undefined): "/v1" | "/v2" {
  return graphQL === "v2" || (isString(graphQL) && graphQL.endsWith("/v2")) ? "/v2" : "/v1"
}

/**
 * Checks if the trimmed GRAPH_QL value represents a branch name override
 * Only called in non-production contexts
 */
function isBranchNameOverride(graphQLTrimmed: string): boolean {
  return (
    graphQLTrimmed !== "" &&
    graphQLTrimmed !== "v1" &&
    graphQLTrimmed !== "v2" &&
    graphQLTrimmed !== "local" &&
    graphQLTrimmed !== "undefined" &&
    !graphQLTrimmed.startsWith("http")
  )
}

/**
 * Builds a branch-based GraphQL URL
 */
function buildBranchUrl(branch: string, envName: string, path: "/v1" | "/v2"): string {
  const slug = branch.startsWith("graphql-") ? branch : `graphql-${branchToSlug(branch)}`
  const finalEnvName = envName === DEVELOPMENT ? DEVELOPMENT : STAGING
  return `https://${slug}.${finalEnvName}.eu-west-1.perkbox.services${path}`
}

/**
 * Appends a path to a base URL, handling trailing slashes correctly
 */
function appendPath(baseUrl: string, path: string): string {
  const separator = baseUrl.endsWith("/") ? "" : "/"
  return `${baseUrl}${separator}${path.slice(1)}`
}

/**
 * Handles HTTP URL overrides, managing existing version paths
 */
function handleHttpUrlOverride(url: string, path: "/v1" | "/v2"): string {
  if (url.endsWith("/v2")) {
    return url
  }
  if (url.endsWith("/v1")) {
    return path === "/v2" ? url.replace("/v1", "/v2") : url
  }
  const urlObj = new URL(url)
  const hasPath = urlObj.pathname !== "/" && urlObj.pathname !== ""
  return hasPath ? url : appendPath(url, path)
}
